import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { getUserProfile } from "../services/authServices";
import { Spin } from "antd";
import { Logger } from '../utils/logger';
import { API_URL } from "../config/config";

export default function PrivateRoute({ children }) {
  const [isLoading, setIsLoading] = useState(true);
  const data = localStorage.getItem("auth_token");

  const refreshToken = async () => {
    try {
      // Get the userData with refresh token
      const userData = JSON.parse(localStorage.getItem("userData") || "{}");
      const refreshToken = userData.refreshToken;
      
      if (!refreshToken) {
        // No refresh token, can't refresh
        return false;
      }
      
      // Call refresh token API
      const response = await axios.post(
        `${API_URL}api/v1/auth/refresh-token`, 
        { refreshToken }
      );
      
      if (response.data?.data?.token) {
        // Update tokens in storage
        localStorage.setItem("auth_token", response.data.data.token);
        
        // Update user data
        userData.token = response.data.data.token;
        userData.refreshToken = response.data.data.refreshToken || userData.refreshToken;
        localStorage.setItem("userData", JSON.stringify(userData));
        
        return true;
      }
      
      return false;
    } catch (error) {
      Logger.log("Token refresh failed", error);
      return false;
    }
  }

  const fetchUserProfile = async () => {
    // Check if we fetched recently (within last 5 minutes)
    const lastFetch = localStorage.getItem("last_profile_fetch");
    const now = Date.now();
    
    if (lastFetch && now - parseInt(lastFetch) < 300000) { // 5 minutes
      setIsLoading(false);
      return;
    }

    try {
      const response = await getUserProfile();

      if (response?.data) {
        localStorage.setItem("userData", JSON.stringify(response?.data?.data));
        localStorage.setItem(
          "user_preferences",
          JSON.stringify(response.data.data.userPreference)
        );
        localStorage.setItem("last_profile_fetch", now.toString());
      }
    } catch (error) {
      Logger.log(`[ERROR] ${error.message}`);
      
      // Handle 401 unauthorized errors
      if (error.response && error.response.status === 401) {
        // Try to refresh the token
        const refreshed = await refreshToken();
        
        if (refreshed) {
          // Try fetching profile again with new token
          try {
            const retryResponse = await getUserProfile();
            if (retryResponse?.data) {
              localStorage.setItem("userData", JSON.stringify(retryResponse?.data?.data));
              localStorage.setItem(
                "user_preferences",
                JSON.stringify(retryResponse.data.data.userPreference)
              );
              localStorage.setItem("last_profile_fetch", now.toString());
              setIsLoading(false);
              return;
            }
          } catch (retryError) {
            // If retry also fails, clear auth and redirect
            localStorage.removeItem("auth_token");
            localStorage.removeItem("userData");
            localStorage.removeItem("user_preferences");
          }
        } else {
          // Refresh failed, clear auth data
          localStorage.removeItem("auth_token");
          localStorage.removeItem("userData");
          localStorage.removeItem("user_preferences");
        }
      }
    }
    setIsLoading(false);
  };

  useEffect(() => {
    if (data) fetchUserProfile();
    else setIsLoading(false);
  }, [data]);

  if (isLoading) {
    return (
      <div className="vh-100 w-100 d-flex align-items-center justify-content-center">
        <Spin size="large" tip="Loading..." spinning={true}></Spin>
      </div>
    );
  }

  return data ? children : <Navigate to="/" />;
}